/**
 * Privacy & security — invitation visibility defaults and active
 * session / device management.
 *
 * InviteCloud uses Firebase OAuth only (Google / Facebook / Microsoft).
 * Email+password authentication is not supported, so no Change Password UI
 * is rendered here. For the same reason there is no InviteCloud-owned
 * two-factor toggle — any 2FA on the account is whatever the OAuth
 * provider itself offers, not something this app can add or control.
 */
import React from 'react';
import { Icons } from '@/src/modules/components/Icons';
import {
  SettingsCard,
  SettingRow,
  ToggleSwitch,
  Pill,
} from '@/src/modules/components/settings/primitives';
import { usePersistentState } from '@/src/modules/pages/settings/storage';

/**
 * Best-effort device/browser label from the UA string. There is no backend
 * session tracking yet (see `UserSession` in schema.prisma — rows are only
 * written on account deletion, never on login), so this is the only real
 * (non-fabricated) signal available: what THIS browser reports about itself.
 * No location is shown — deriving one honestly needs either a Geolocation
 * permission prompt or server-side IP lookup, neither of which exists here.
 */
function detectCurrentDevice(): string {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return 'This device';
  const ua = navigator.userAgent;

  // iOS UAs also contain "like Mac OS X" for compatibility, so they must be
  // checked before the generic "Mac OS X" test.
  let os = 'Unknown device';
  if (/Windows/.test(ua)) os = 'Windows PC';
  else if (/iPad/.test(ua)) os = 'iPad';
  else if (/iPhone/.test(ua)) os = 'iPhone';
  else if (/Mac OS X/.test(ua)) os = 'Mac';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Linux/.test(ua)) os = 'Linux';

  // Order matters: Edge/Opera UAs also contain "Chrome"/"Safari" tokens.
  let browser = '';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\//.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  return browser ? `${os} · ${browser}` : os;
}

export function PrivacySecuritySection() {
  const [publicByDefault, setPublicByDefault] = usePersistentState('publicInvites', false);

  return (
    <div className="flex flex-col gap-6">
      <SettingsCard
        icon={Icons.Shield}
        title="Account protection"
        description="Extra layers of security for your account and invitations."
      >
        <SettingRow
          label="Public invitations by default"
          description="New invitations are discoverable without a direct link."
          htmlFor="sec-public"
        >
          <ToggleSwitch
            id="sec-public"
            checked={publicByDefault}
            onChange={setPublicByDefault}
          />
        </SettingRow>
      </SettingsCard>

      <SettingsCard
        icon={Icons.Device}
        title="Login sessions &amp; devices"
        description="The device you're currently signed in on."
      >
        <div className="flex items-center gap-3 py-1">
          <span className="w-9 h-9 rounded-xl bg-surface-low text-on-surface-variant flex items-center justify-center">
            <Icons.Monitor size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-on-surface truncate">
                {detectCurrentDevice()}
              </p>
              <Pill tone="primary">This device</Pill>
            </div>
            <p className="text-xs text-on-surface-variant">Active now</p>
          </div>
        </div>
        <p className="text-xs text-on-surface-variant mt-3">
          Viewing and managing sessions on other devices isn't available yet.
        </p>
      </SettingsCard>
    </div>
  );
}
